import React, { useCallback, useEffect, useState } from "react";

import * as api from "./api";
import Shell, { type Section } from "./components/Shell";
import { effectiveRole as computeEffectiveRole } from "./logic";
import SignIn from "./SignIn";
import Support from "./pages/Support";
import Users from "./pages/Users";
import Orgs from "./pages/Orgs";
import Conversations from "./pages/Conversations";
import Reconciliation from "./pages/Reconciliation";
import Audit from "./pages/Audit";
import Cost from "./pages/Cost";
import type { NavIntent, PageProps } from "./pages/types";

export default function App() {
  const [authed, setAuthed] = useState(api.isStaff());
  const [signedIn, setSignedIn] = useState(!!api.getToken());

  if (!authed) {
    return (
      <SignIn
        signedIn={signedIn}
        onSignedIn={() => {
          setSignedIn(true);
          setAuthed(api.isStaff());
        }}
        onSignOut={() => {
          api.signOut();
          setSignedIn(false);
          setAuthed(false);
        }}
      />
    );
  }
  return (
    <Console
      onSignOut={() => {
        api.signOut();
        setAuthed(false);
        setSignedIn(false);
      }}
    />
  );
}

function Console({ onSignOut }: { onSignOut: () => void }) {
  const [section, setSection] = useState<Section>("support");
  const [viewAsStaff, setViewAsStaff] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [intent, setIntent] = useState<NavIntent | null>(null);

  const realRole = (api.getPlatformRole() as api.PlatformRole) || "NONE";
  const effRole = computeEffectiveRole(realRole, viewAsStaff);
  const operator = api.getOperator();

  // Auto-clear the transient toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const navigate = useCallback((s: Section, nextIntent?: NavIntent) => {
    setIntent(nextIntent || null);
    setSection(s);
    setToast(null);
  }, []);

  const showToast = useCallback((msg: string) => setToast(msg), []);

  const pageProps: PageProps = {
    realRole,
    effectiveRole: effRole,
    showToast,
    navigate,
    intent,
  };

  let body: React.ReactNode = null;
  switch (section) {
    case "support":
      body = <Support {...pageProps} />;
      break;
    case "users":
      body = <Users {...pageProps} />;
      break;
    case "orgs":
      body = <Orgs {...pageProps} />;
      break;
    case "conversations":
      body = <Conversations {...pageProps} />;
      break;
    case "reconciliation":
      body = <Reconciliation {...pageProps} />;
      break;
    case "audit":
      body = <Audit {...pageProps} />;
      break;
    case "cost":
      body = <Cost {...pageProps} />;
      break;
  }

  return (
    <Shell
      section={section}
      onNavigate={(s) => navigate(s)}
      operatorName={operator.name}
      operatorInitials={operator.initials}
      operatorRoleLabel={realRole}
      isSuperadmin={realRole === "SUPERADMIN"}
      viewAsStaff={viewAsStaff}
      onToggleViewAs={setViewAsStaff}
      onSignOut={onSignOut}
      toast={toast}
      onDismissToast={() => setToast(null)}
    >
      {body}
    </Shell>
  );
}
